import { useCallback, useMemo } from "react";
import {
    buildDraftRefDedupKey,
    usePendingReferenceContextMaybe,
    useSpreadsheetRefContextMaybe,
} from "./SpreadsheetRefContext";
import { useTasksContextMaybe } from "./tasks.context";
import { DraftRefsTarget } from "./useDraftRefsByTarget";

/**
 * Adds the selected spreadsheet cells to the open task's chat (main composer
 * by default) and shows the chat tab. Shared by the report command bars and
 * the cell drag. `pendingReference` is null while the selected cells are
 * already a draft chip in the main composer, so removing the chip offers them
 * again.
 */
export function useAddCellsToChat() {
    const refCtx = useSpreadsheetRefContextMaybe();
    const pendingRefCtx = usePendingReferenceContextMaybe();
    const tasksCtx = useTasksContextMaybe();

    const selectedReference = refCtx
        ? (pendingRefCtx?.pendingReference ?? null)
        : null;
    const mainDraftRefs = refCtx?.draftRefsByTarget.main;

    const pendingReference = useMemo(() => {
        if (!selectedReference || !mainDraftRefs) {
            return selectedReference;
        }
        const selectedKey = buildDraftRefDedupKey(selectedReference);
        const isDrafted = mainDraftRefs.some(
            (ref) => buildDraftRefDedupKey(ref) === selectedKey,
        );
        return isDrafted ? null : selectedReference;
    }, [selectedReference, mainDraftRefs]);

    const addCellsToChat = useCallback(
        (target: DraftRefsTarget = "main") => {
            if (!refCtx || !pendingReference) {
                return;
            }
            refCtx.appendDraftRefs(target, [pendingReference]);
            tasksCtx?.showTaskDetailPanel("chat");
        },
        [refCtx, pendingReference, tasksCtx],
    );

    return { pendingReference, addCellsToChat };
}
